<template>
  <section class="ppt-purchase">
    <header>
      <h1 class="page-header">Purchase</h1>
    </header>
    <errors-alert
      v-bind:errors="errorsFromBackend"
      v-on:clear="clearAlert()"
    />
    <section class="alert alert-info">
      <section v-if="daysLeftOnFreeTrial > 0">
        <p>You have <strong>{{ daysLeftOnFreeTrial }} days left</strong> on your 14 day free trial. You can certainly wait for your free trial to end before purchasing, but if you'd like to purchase now, fill out and submit the form below.</p>
      </section>
      <section v-else>
        <p>Your 14 day free trial has ended. Purchase either the basic or the premium version to continue using Six Plus Hold’em.</p>
      </section>
      <p>Note: If you purchase the basic version and later want to upgrade to the premium version, you'll only have to pay the $50 difference, not the full $75.</p>
      <p>Any questions? Check out the <router-link to="/faq">FAQ</router-link>, or <router-link to="/contact">get in touch</router-link>.</p>
    </section>
    <section v-if="!purchasedShortDeck" class="alert alert-info">
      Interested in short deck? Head over to the <router-link to="/purchase-short-deck">short deck purchase page</router-link>.
    </section>
    <pricing-table />
    <hr>
    <label>Version</label>
    <section class="btn-group btn-group-lg btn-group-justified">
      <a
        class="btn btn-default"
        v-bind:class="{ active: version === 'basic' }"
        v-bind:disabled="purchasing"
        v-on:click="version = 'basic'"
      >
        Basic ($25)
      </a>
      <a
        class="btn btn-default"
        v-bind:class="{ active: version === 'premium' }"
        v-bind:disabled="purchasing"
        v-on:click="version = 'premium'"
      >
        Premium ($75)
      </a>
    </section>
    <br>
    <section class="form-group">
      <label>
        Credit card number
        <card-number
          ref="cardNumber"
          class="stripe-element card-number form-control"
          v-bind:stripe="stripeConsumerKey"
          v-bind:options="options"
          v-on:change="number = $event.complete"
        />
      </label>
    </section>
    <section class="form-group">
      <label>
        Expiration date
        <card-expiry
          ref="cardExpiry"
          class="stripe-element card-expiry form-control"
          v-bind:stripe="stripeConsumerKey"
          v-bind:options="options"
          v-on:change="expiry = $event.complete"
        />
      </label>
    </section>
    <section class="form-group">
      <label>
        CVC
        <card-cvc
          ref="cardCvc"
          class="stripe-element card-cvc form-control"
          v-bind:stripe="stripeConsumerKey"
          v-bind:options="options"
          v-on:change="cvc = $event.complete"
        />
      </label>
    </section>
    <button
      class="pay-with-stripe btn btn-primary btn-block btn-lg"
      v-bind:disabled="!complete || purchasing"
      v-on:click="purchase()"
    >
      {{ purchaseText }}
    </button>
  </section>
</template>

<script>
import ErrorsAlert from '@/components/utilities/errors-alert';
import PricingTable from '@/components/utilities/pricing-table';
import { CardNumber, CardExpiry, CardCvc, createToken } from 'vue-stripe-elements-plus';
import utilities from '@/services/utilities/utilities';

export default {
  name: 'ppt-purchase',
  components: {
    'errors-alert': ErrorsAlert,
    'pricing-table': PricingTable,
    'card-number': CardNumber,
    'card-expiry': CardExpiry,
    'card-cvc': CardCvc,
  },
  data: function () {
    return {
      stripeConsumerKey: process.env.STRIPE_CONSUMER_KEY,
      options: {},
      complete: false,
      version: 'basic',
      number: false,
      expiry: false,
      cvc: false,
      purchasing: false,
      errorsFromBackend: [],
    };
  },
  computed: {
    daysLeftOnFreeTrial: function () {
      return this.$store.getters['account/daysLeftOnFreeTrial'];
    },
    purchaseText: function () {
      if (this.purchasing) {
        return 'Purchasing...';
      }

      let price = this.version === 'basic' ? '$25' : '$75';

      return `Purchase the ${this.version} version for ${price}`;
    },
    purchasedShortDeck: function () {
      return this.$store.getters['account/purchasedShortDeck'];
    },
  },
  watch: {
    number: function () {
      this.update();
    },
    expiry: function () {
      this.update();
    },
    cvc: function () {
      this.update();
    },
  },
  beforeRouteLeave: function (to, from, next) {
    this.$destroy(); // because of stripe issue
    next();
  },
  methods: {
    update: function () {
      this.complete = this.number && this.expiry && this.cvc;

      // field completed, find field to focus next
      if (this.number) {
        if (!this.expiry) {
          this.$refs.cardExpiry.focus();
        } else if (!this.cvc) {
          this.$refs.cardCvc.focus();
        }
      } else if (this.expiry) {
        if (!this.cvc) {
          this.$refs.cardCvc.focus()
        } else if (!this.number) {
          this.$refs.cardNumber.focus();
        }
      }
      // no focus magic for the CVC field as it gets complete with three
      // numbers, but can also have four
    },
    purchase: function () {
      let self = this;

      self.purchasing = true;
      createToken()
        .then(function (result) {
          if (result.error) {
            self.purchasing = false;
            self.errorsFromBackend.push(result.error.message);
          }

          else {
            self.$http
              .patch(`${process.env.BASE_API_URL}/user/purchase-${self.version}`, {
                token: result.token.id,
              })
              .then(function () {
                self.$store.commit('account/purchasePPT', self.version);
                self.purchasing = false;
                alert(`Successfully purchased the ${self.version} version of Six Plus Hold’em!`);
                self.$router.push({ path: '/account' });
              })
              .catch(function (e) {
                self.purchasing = false;
                utilities.general.handleNetworkError(e);
              })
              .then(function () {
                if (process.env.NODE_ENV === 'production') {
                  $FPROM.trackSignup({
                   email: self.$store.state.account.user.email,
                  }, function() {
                    console.log('Callback received!');
                  });
                  rdt('track', 'Purchase');
                  // self.gtagReportConversion();
                  self.microsoftReportConversion();
                }
              })
              .catch(function () {
                console.log('Error with the conversion tracking for ads.');
              });
            ;
          }
        })
        .catch(function (e) {
          self.purchasing = false;
          utilities.general.handleNetworkError(e);
        })
      ;
    },
    gtagReportConversion: function (url) {
      var callback = function () {
        if (typeof(url) != 'undefined') {
          window.location = url;
        }
      };
      gtag('event', 'conversion', {
          'send_to': 'AW-714944788/w5PhCJ3ozqgBEJTi9NQC',
          'transaction_id': '',
          'event_callback': callback
      });
      return false;
    },
    microsoftReportConversion: function () {
      let eventValue = this.version === 'basic' ? 25 : 75;

      window.uetq = window.uetq || [];
      window.uetq.push('event', 'Subscribe', { event_value: eventValue });
    },
    clearAlert: function () {
      this.errorsFromBackend.splice(0, this.errorsFromBackend.length);
    },
  },
};
</script>

<style lang="scss" scoped>
  .ppt-purchase {
    font-size: 18px;
  }

  button {
    font-size: 18px;
  }

  .credit-card-inputs.complete {
    border: 2px solid green;
  }

  label {
    width: 100%;
  }

  .form-control {
    width: 100%;
    height: 40px;
  }
</style>



// WEBPACK FOOTER //
// src/components/user/purchase.vue